import { createTokenStorage, SecureTokenStorage, TokenInfo } from './tokenStorage';
import { getLoginRedirectUrl, config } from './config';

export interface AuthUser {
  email: string;
  isAdmin: boolean;
  allowedProducts: string[];
}

export interface LoginResult {
  success: boolean;
  user?: AuthUser;
  error?: string;
}

/**
 * Authentication service for managing secure user sessions
 * Uses httpOnly cookies for token storage to prevent XSS attacks
 */
export class AuthService {
  private static instance: AuthService;
  private tokenStorage: SecureTokenStorage;
  private baseUrl: string;
  private currentUser: AuthUser | null = null;

  private constructor() {
    this.baseUrl = config.apiBaseUrl;
    this.tokenStorage = createTokenStorage(this.baseUrl);
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Get the current authentication token
   */
  async getToken(): Promise<string | null> {
    return await this.tokenStorage.getToken();
  }

  /**
   * Check if user is currently authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const token = await this.getToken();
    const hasUser = !!this.currentUser;
    return !!token && hasUser;
  }

  /**
   * Get current user information
   */
  getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  /**
   * Login with Access Group token
   * This assumes the frontend receives a token from Access Group authentication
   */
  async loginWithToken(accessGroupToken: string): Promise<LoginResult> {
    try {
      // Validate token and get user info by calling our API
      const response = await fetch(`${this.baseUrl}/auth/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessGroupToken}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        return {
          success: false,
          error: 'Invalid authentication token'
        };
      }

      const userData = await response.json();
      
      // Store token securely
      const tokenInfo: TokenInfo = {
        token: accessGroupToken,
        expiresAt: userData.expiresAt || Date.now() + (24 * 60 * 60 * 1000) // 24 hours default
      };

      await this.tokenStorage.setToken(tokenInfo);

      this.currentUser = {
        email: userData.email,
        isAdmin: userData.isAdmin,
        allowedProducts: userData.allowedProducts
      };

      return {
        success: true,
        user: this.currentUser
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'Authentication failed'
      };
    }
  }

  /**
   * Logout user and clear tokens
   */
  async logout(): Promise<void> {
    try {
      await this.tokenStorage.clearToken();
      this.currentUser = null;
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<boolean> {
    try {
      const newToken = await this.tokenStorage.refreshToken();
      return !!newToken;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  /**
   * Initialize authentication state on app startup
   */
  async initialize(): Promise<void> {
    try {
      // Just check if we have a valid token, don't try to validate session yet
      // This avoids redirect loops during app initialization
      const token = await this.getToken();
      if (token) {
        // Get user data from the token API
        try {
          const response = await fetch(`${this.baseUrl}/auth/token`, {
            method: 'GET',
            credentials: 'include'
          });
          
          if (response.ok) {
            const tokenData = await response.json();
            this.currentUser = {
              email: tokenData.user?.email || 'unknown@accessgroup.com',
              isAdmin: tokenData.user?.isAdmin || false,
              allowedProducts: []
            };
          } else {
            // Fallback if token API fails
            this.currentUser = {
              email: 'unknown@accessgroup.com',
              isAdmin: false,
              allowedProducts: []
            };
          }
        } catch (error) {
          console.warn('Failed to get user data from token API:', error);
          this.currentUser = {
            email: 'unknown@accessgroup.com',
            isAdmin: false,
            allowedProducts: []
          };
        }
      } else {
        this.currentUser = null;
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      this.currentUser = null;
      // Don't redirect on initialization errors to avoid loops
    }
  }

  /**
   * Validate current session and update user info
   * Only call this when you need to explicitly validate the session
   */
  private async validateCurrentSession(): Promise<boolean> {
    try {
      const token = await this.getToken();
      if (!token) {
        this.currentUser = null;
        return false;
      }

      // Since we don't have an /auth/user endpoint, we'll just check if the token is valid
      // by trying to get it. The getToken() method already validates the token
      this.currentUser = {
        email: 'user@accessgroup.com', // Placeholder until we get real user data
        isAdmin: false,
        allowedProducts: []
      };
      
      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      this.currentUser = null;
      return false;
    }
  }

  /**
   * Redirect to Access Group login page
   */
  private redirectToLogin(): void {
    const redirectUrl = getLoginRedirectUrl();
    
    window.location.href = redirectUrl;
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();